/**
 * OrgContext.tsx — Multi-org session management for billing admin
 *
 * Provides org selection state, org switcher, and org-scoped API base URL.
 * After OIDC login, if the user has multiple orgs, they see an OrgSelectorPage.
 * If only one org, they're auto-redirected to that org's dashboard.
 *
 * @module packages/billing-admin/src/contexts/OrgContext
 * @since 2026-06-19 (SaaS conversion — Phase 8)
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAdminAuth } from './AdminAuthContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OrgInfo {
  orgId: string;
  orgName: string;
  slug: string;
  role: 'admin' | 'member';
  status: string;
}

export interface OrgContextState {
  /** List of orgs the user has billing access to. */
  availableOrgs: OrgInfo[];
  /** Currently selected org, or null if not yet selected. */
  activeOrg: OrgInfo | null;
  /** Select an org by ID. */
  selectOrg: (orgId: string) => void;
  /** Clear selection (return to org selector). */
  clearOrg: () => void;
  /** Whether the user has platform admin access. */
  isPlatformAdmin: boolean;
  /** Whether orgs are still loading. */
  loading: boolean;
  /** Error from loading orgs. */
  error: string | null;
}

const OrgContextReact = createContext<OrgContextState | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function OrgContextProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { token, apiBaseUrl, user } = useAdminAuth();
  const [availableOrgs, setAvailableOrgs] = useState<OrgInfo[]>([]);
  const [activeOrg, setActiveOrg] = useState<OrgInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isPlatformAdmin = useMemo(() => {
    if (!user) return false;
    return user.permissions.includes('billing:platform:admin') ||
           user.permissions.includes('platform.admin');
  }, [user]);

  // Fetch available orgs on mount / token change
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    fetch(`${apiBaseUrl}/api/v1/users/me/orgs`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Failed to fetch orgs: ${res.status}`);
        const data = (await res.json()) as { orgs: OrgInfo[] };
        setAvailableOrgs(data.orgs ?? []);

        // Auto-select if only one org
        if (data.orgs.length === 1) {
          setActiveOrg(data.orgs[0]!);
        } else if (data.orgs.length > 1) {
          // Restore from session storage
          const savedOrgId = sessionStorage.getItem('billing_active_org');
          if (savedOrgId) {
            const saved = data.orgs.find((o) => o.orgId === savedOrgId);
            if (saved) setActiveOrg(saved);
          }
        }
      })
      .catch((err) => {
        // Graceful fallback: if the endpoint isn't available yet,
        // create a synthetic org from the JWT claims so existing UI still works
        console.warn('[OrgContext] Failed to load orgs, using fallback:', err);
        if (user) {
          const fallbackOrg: OrgInfo = {
            orgId: user.tenantId ?? 'default',
            orgName: 'My Organization',
            slug: 'default',
            role: 'admin',
            status: 'active',
          };
          setAvailableOrgs([fallbackOrg]);
          setActiveOrg(fallbackOrg);
        }
        setError(null); // Don't surface error since fallback works
      })
      .finally(() => setLoading(false));
  }, [token, apiBaseUrl]);

  const selectOrg = useCallback((orgId: string) => {
    const org = availableOrgs.find((o) => o.orgId === orgId);
    if (org) {
      setActiveOrg(org);
      sessionStorage.setItem('billing_active_org', orgId);
    }
  }, [availableOrgs]);

  const clearOrg = useCallback(() => {
    setActiveOrg(null);
    sessionStorage.removeItem('billing_active_org');
  }, []);

  const value = useMemo<OrgContextState>(() => ({
    availableOrgs,
    activeOrg,
    selectOrg,
    clearOrg,
    isPlatformAdmin,
    loading,
    error,
  }), [availableOrgs, activeOrg, selectOrg, clearOrg, isPlatformAdmin, loading, error]);

  return (
    <OrgContextReact.Provider value={value}>
      {children}
    </OrgContextReact.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useOrgContext(): OrgContextState {
  const ctx = useContext(OrgContextReact);
  if (!ctx) throw new Error('useOrgContext must be used within OrgContextProvider');
  return ctx;
}
